/**
 * CustomerDependentsTab.tsx
 * Component độc lập hiển thị danh sách Người phụ thuộc của khách hàng.
 */
import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Icon } from '../Icon';
import { colors, spacing, fontWeights, borderRadius } from '../../theme';
import type { CustomerDependent } from '../../data/customers';

export interface CustomerDependentsTabProps {
  dependents: CustomerDependent[];
}

const RELATIONSHIP_LABEL: Record<string, string> = {
  spouse: 'Vợ/Chồng',
  child: 'Con',
  parent: 'Bố/Mẹ',
  other: 'Khác',
};

export const CustomerDependentsTab: React.FC<CustomerDependentsTabProps> = ({ dependents }) => {
  if (!dependents || dependents.length === 0) {
    return (
      <View style={styles.emptyBox}>
        <Icon name="groups" size={32} color={colors.outlineVariant} />
        <Text style={styles.emptyText}>Chưa có người phụ thuộc</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {dependents.map((d) => (
        <View key={d.id} style={styles.dependentCard}>
          <View style={[styles.dependentAvatar, { backgroundColor: d.gender === 'Nữ' ? '#EC4899' : '#3B82F6' }]}>
            <Text style={styles.dependentAvatarText}>{d.name.charAt(0)}</Text>
          </View>
          <View style={{ flex: 1 }}>
            <View style={styles.dependentHeaderRow}>
              <Text style={styles.dependentName}>{d.name}</Text>
              <View style={styles.relBadge}>
                <Text style={styles.relBadgeText}>{RELATIONSHIP_LABEL[d.relationship] || d.relationship}</Text>
              </View>
            </View>
            <Text style={styles.dependentSub}>
              {d.dob} ({d.age} tuổi) • {d.gender}
            </Text>
            {d.occupation && <Text style={styles.dependentOcc}>Nghề nghiệp: {d.occupation}</Text>}
          </View>
        </View>
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: spacing[3],
  },
  emptyBox: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing[8],
    gap: spacing[2],
  },
  emptyText: {
    fontSize: 13,
    color: colors.onSurfaceVariant,
  },
  dependentCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[3],
    padding: spacing[3],
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.DEFAULT,
    borderWidth: 1,
    borderColor: colors.outlineVariant,
  },
  dependentAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dependentAvatarText: {
    color: '#ffffff',
    fontWeight: fontWeights.bold,
    fontSize: 16,
  },
  dependentHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  dependentName: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
  },
  relBadge: {
    backgroundColor: '#F3F4F6',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: borderRadius.full,
  },
  relBadgeText: {
    fontSize: 11,
    color: colors.onSurfaceVariant,
    fontWeight: fontWeights.medium,
  },
  dependentSub: {
    fontSize: 12,
    color: colors.onSurfaceVariant,
    marginTop: 2,
  },
  dependentOcc: {
    fontSize: 11,
    color: colors.onSurfaceVariant,
    marginTop: 2,
  },
});
